
import { computed, defineComponent, ref } from 'vue'
import {
  PermissionDetail,
  PermissionFeMap,
  ServicePermissionService, toSubscribeByPriceId,
  BASIC_SERVICE_PERMISSION_NAMES
} from '@/services/service-permission.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { countdownDay } from '@/utils/time'
import { ServiceType } from '@/views/dashboard/_enum/service-type'
import { isObjectEmpty } from '@/utils/tool'
import { ElMessage } from 'element-plus'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { useI18n } from 'vue-i18n'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

export default defineComponent({
  /** 注意：
   * 如果是双专区的服务，且单个终端应用场景时，
   * 需要按照系统类型传normalPermissionKey 或 linuxPermissionKey 其中一个，
   * 不可两者都传，不然授权会优先按normalPermissionKey授权
   * 入参示例参考：
   * :normal-permission-key="terminalInfo.system_type !== TerminalType.linux ? ModulePermissionKey[AreaType.normal] : undefined"
   * :linux-permission-key="terminalInfo.system_type === TerminalType.linux ? ModulePermissionKey[AreaType.linux] : undefined"
   */
  props: {
    normalPermissionKey: {
      type: String,
      default: ''
    },
    linuxPermissionKey: {
      type: String,
      default: ''
    },
    authStatus: {
      type: Number,
      default: 0
    },
    deviceId: {
      type: String,
      default: ''
    },
  },
  setup (props, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([])
    const servicePermissionService = injector.get(ServicePermissionService)

    const normalPermissionInfo = ref({} as PermissionDetail)
    const linuxPermissionInfo = ref({} as PermissionDetail)
    const normalPermissionConfig = ref({} as PermissionDetail)
    const linuxPermissionConfig = ref({} as PermissionDetail)

    const permissionKey = computed(() => {
      return props.normalPermissionKey || props.linuxPermissionKey
    })

    const permissionInfo = computed(() => {
      return isObjectEmpty(normalPermissionInfo.value) ? linuxPermissionInfo.value : normalPermissionInfo.value
    })

    const isBasicServiceInJP = computed(() => {
      return IS_JP_ONLY_BUILD && BASIC_SERVICE_PERMISSION_NAMES.has(permissionKey.value)
    })

    servicePermissionService.rightsMap.subscribe(() => {
      normalPermissionInfo.value = servicePermissionService.getPermissionInfo(props.normalPermissionKey)
      linuxPermissionInfo.value = servicePermissionService.getPermissionInfo(props.linuxPermissionKey)
    })

    servicePermissionService.allRightsMap.subscribe(() => {
      normalPermissionConfig.value = servicePermissionService.getAllPermissionInfo(props.normalPermissionKey)
      linuxPermissionConfig.value = servicePermissionService.getAllPermissionInfo(props.linuxPermissionKey)
    })

    const isShowExperience = computed(() => {
      if (props.normalPermissionKey && !props.linuxPermissionKey) {
        return normalPermissionInfo.value.type === ServiceType.experience
      } else if (!props.normalPermissionKey && props.linuxPermissionKey) {
        return linuxPermissionInfo.value.type === ServiceType.experience
      } else if (props.normalPermissionKey && props.linuxPermissionKey) {
        return normalPermissionInfo.value.type === ServiceType.experience && linuxPermissionInfo.value.type === ServiceType.experience
      }
    })

    const isShowExpire = computed(() => {
      if (props.normalPermissionKey && !props.linuxPermissionKey) {
        return isObjectEmpty(normalPermissionInfo.value)
      } else if (!props.normalPermissionKey && props.linuxPermissionKey) {
        return isObjectEmpty(linuxPermissionInfo.value)
      } else if (props.normalPermissionKey && props.linuxPermissionKey) {
        return isObjectEmpty(normalPermissionInfo.value) && isObjectEmpty(linuxPermissionInfo.value)
      }
    })

    function handleAuth () {
      servicePermissionService.authTerminal({
        permission_names: [permissionKey.value],
        device_ids: [props.deviceId],
        is_auth: true,
      }).then(() => {
        ElMessage.success(t('serviceSubscribe.detailsBarAuthSuccess'))
        context.emit('authSuccess')
      })
    }

    function toSubscribe () {
      toSubscribeByPriceId({
        normal_price_id: normalPermissionConfig.value.price_id,
        linux_price_id: linuxPermissionConfig.value.price_id,
      })
    }

    return {
      TerminalAuthStatus,
      ServiceType,
      PermissionFeMap,
      normalPermissionInfo,
      permissionKey,
      permissionInfo,
      normalPermissionConfig,
      linuxPermissionConfig,
      isShowExperience,
      isShowExpire,
      isBasicServiceInJP,
      countdownDay,
      toSubscribe,
      isObjectEmpty,
      handleAuth,
    }
  }
})
